import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, ChevronLeft, AlertCircle, PenLine } from "lucide-react";
import { getBoardEntityLabel } from "@/lib/board-types";

interface Template {
  id: string;
  boardId: string;
  title: string;
  description: string | null;
  priority: string | null;
  type: string | null;
  labelIds: string[];
  createdAt: string;
}

export interface PickerBoard {
  id: string;
  name: string;
  userRole: string;
}

interface TemplatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId?: string;
  boards?: PickerBoard[];
  activeBoardId?: string | null;
  onSelect: (templateId: string | null, boardId?: string, templateData?: { title: string; description: string | null; priority: string | null; labelIds: string[] }) => void;
  boardType?: string;
}

export function TemplatePicker({ open, onOpenChange, boardId, boards, activeBoardId, onSelect, boardType }: TemplatePickerProps) {
  const isMultiBoard = !!boards && boards.length > 0;
  const creatableBoards = isMultiBoard ? boards.filter(b => b.userRole === "admin" || b.userRole === "editor") : [];
  const hasMultipleCreatable = creatableBoards.length > 1;

  const [step, setStep] = useState<"board" | "template">("board");
  const [selectedBoardId, setSelectedBoardId] = useState<string>(boardId ?? "");

  useEffect(() => {
    if (!open) {
      setStep("board");
      setSelectedBoardId(boardId ?? "");
      return;
    }

    if (isMultiBoard) {
      if (activeBoardId) {
        const activeBoard = creatableBoards.find(b => b.id === activeBoardId);
        if (activeBoard) {
          setSelectedBoardId(activeBoardId);
          setStep("template");
          return;
        }
      }
      if (creatableBoards.length === 1) {
        setSelectedBoardId(creatableBoards[0].id);
        setStep("template");
        return;
      }
      setStep("board");
    } else if (boardId) {
      setSelectedBoardId(boardId);
      setStep("template");
    }
  }, [open]);

  const { data, isLoading } = useQuery<{ templates: Template[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "templates"],
    enabled: open && !!selectedBoardId && step === "template",
  });

  const templates = data?.templates ?? [];

  useEffect(() => {
    if (!open || step !== "template" || isLoading || !data) return;
    if (!selectedBoardId) return;

    if (templates.length === 0 && boardType === "regular") {
      onSelect(null, selectedBoardId);
      onOpenChange(false);
      return;
    }
    if (templates.length === 1 && boardType !== "regular") {
      onSelect(templates[0].id, selectedBoardId, { title: templates[0].title, description: templates[0].description, priority: templates[0].priority, labelIds: templates[0].labelIds ?? [] });
      onOpenChange(false);
    }
  }, [open, step, isLoading, data, templates.length, selectedBoardId]);

  if (!open) return null;

  function handleBoardSelect(bId: string) {
    setSelectedBoardId(bId);
    setStep("template");
  }

  function handleBackToBoards() {
    setStep("board");
    setSelectedBoardId("");
  }

  function handleTemplateSelect(templateId: string) {
    const tmpl = templates.find(t => t.id === templateId);
    onSelect(templateId, selectedBoardId, tmpl ? { title: tmpl.title, description: tmpl.description, priority: tmpl.priority, labelIds: tmpl.labelIds ?? [] } : undefined);
    onOpenChange(false);
  }

  function handleStartFromScratch() {
    onSelect(null, selectedBoardId);
    onOpenChange(false);
  }

  if (isMultiBoard && step === "board") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="template-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="text-template-picker-title">Select a Board</DialogTitle>
            <DialogDescription>Choose which board to create a new {getBoardEntityLabel(boardType).toLowerCase()} on.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2" data-testid="template-picker-board-list">
            {boards!.map(b => {
              const canCreate = b.userRole === "admin" || b.userRole === "editor";
              return (
                <button
                  key={b.id}
                  className={`w-full text-left p-3 rounded-md border flex items-center justify-between gap-3 ${canCreate ? "hover-elevate cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                  onClick={() => canCreate && handleBoardSelect(b.id)}
                  disabled={!canCreate}
                  data-testid={`button-picker-board-${b.id}`}
                >
                  <span className="font-medium text-sm truncate">{b.name}</span>
                  {!canCreate && (
                    <Badge variant="secondary" className="text-xs shrink-0" data-testid={`badge-view-only-${b.id}`}>View Only</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === "template" && isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="template-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="text-template-picker-title">Create New {getBoardEntityLabel(boardType)}</DialogTitle>
            <DialogDescription>Loading templates...</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === "template" && data && templates.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="template-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="text-template-picker-title">Create New {getBoardEntityLabel(boardType)}</DialogTitle>
          </DialogHeader>
          {isMultiBoard && hasMultipleCreatable && (
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={handleBackToBoards}
              data-testid="button-change-board"
            >
              <ChevronLeft className="w-4 h-4" />
              Change board
            </button>
          )}
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="template-picker-empty">
            <AlertCircle className="w-10 h-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No templates available for this board. Ask a board admin to create one in the Templates tab of Board Admin.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === "template" && (templates.length >= 2 || (templates.length === 1 && boardType === "regular"))) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="template-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="text-template-picker-title">Create New {getBoardEntityLabel(boardType)}</DialogTitle>
            <DialogDescription>Choose a template to get started.</DialogDescription>
          </DialogHeader>

          {isMultiBoard && hasMultipleCreatable && (
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleBackToBoards}
              data-testid="button-change-board"
            >
              <ChevronLeft className="w-4 h-4" />
              Change board
            </button>
          )}

          <div className="space-y-2 mt-1" data-testid="template-picker-list">
            {boardType === "regular" && (
              <button
                className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
                onClick={handleStartFromScratch}
                data-testid="button-start-from-scratch"
              >
                <PenLine className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm">Start from scratch</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Create a blank task without a template</div>
                </div>
              </button>
            )}
            {templates.map(t => (
              <button
                key={t.id}
                className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
                onClick={() => handleTemplateSelect(t.id)}
                data-testid={`button-template-${t.id}`}
              >
                <FileText className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm" data-testid={`text-template-title-${t.id}`}>{t.title}</div>
                  {t.description && (
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid={`text-template-description-${t.id}`}>
                      {t.description}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
}
